import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "../../assets/icons/closeIcon.svg";
import { ImportTemplatesPopupProps } from "./email-letter-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { StepIconProps } from "@mui/material/StepIcon";
import { Checkbox } from "@mui/material";

const steps = ["Import File", "Select File"];
const CustomStepIcon: React.FC<StepIconProps> = (props) => {
  const { active, completed, icon } = props;

  return (
    <div
      className={`custom-step-icon ${active ? "active" : ""} ${completed ? "completed" : ""}`}
    >
      <span>{icon}</span>
    </div>
  );
};

const ImportTemplatesPopup: React.FC<ImportTemplatesPopupProps> = ({
  isOpen,
  onClose,
  onCompleteImport,
  onContinueImport,
  title = "Import Email Templates",
  file,
  setFile,
  importedTemplates,
  selectedTemplates = [],
  setSelectedTemplates,
  activeStep = 0,
  setActiveStep,
  errorMessage = "",
  setErrorMessage,
  templateType = "Email",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplates((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!file.type.includes("xml")) {
        setErrorMessage?.("Invalid file type. Please upload a valid XML file");
        return;
      }
      console.log("Selected file:", file);
      setFile(file);
      setErrorMessage?.("");
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleContinue = () => {
    if (activeStep === steps.length - 1) {
      handleCancel();
    } else {
      setActiveStep?.((prev) => prev + 1);
    }
    onContinueImport();
  };

  const checkDisableContinueAndImportButton = () => {
    if (activeStep === 0) {
      return !file || errorMessage !== "";
    }
    if (activeStep === 1) {
      return selectedTemplates?.length === 0;
    }
    return false;
  }

  return (
    <div className="import-templates-popup-wrapper mass-update-wrapper">
      <Dialog
        open={isOpen}
        aria-labelledby="import-templates-dialog-title"
        aria-describedby="import-templates-dialog-description"
        className="update-popup-wrapper import-templates-popup"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "600px", // 90% width on mobile
              maxWidth: "95vw", // prevent overflow
              minHeight: "280px",
              borderBottomLeftRadius: "30px",
              borderBottomRightRadius: "30px",
              top: 0, // add spacing from top on mobile
              margin: 0,
              position: "absolute",
            },
          },
        }}
      >
        <DialogTitle id="import-templates-dialog-title">
          <div className="header-wrapper">
            <div>{title}</div>
            <div className="image-wrapper">
              <img
                src={CloseIcon}
                className="close-icon"
                onClick={handleCancel}
              />
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <Box className="stepper-wrapper">
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={index} completed={activeStep > index}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    onClick={() => {
                      if (index === 0) setActiveStep?.(index);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          {activeStep === 0 && (
            <div className="import-templates-content">
              <p style={{ marginTop: "0px" }}>
                {`You can import your ${templateType?.toLocaleLowerCase()} templates from another account. The
                file must be in the proper XML format.`}
              </p>
              <input
                id={`file-import`}
                type="file"
                accept="application/xml"
                onChange={handleFileChange}
                style={{ display: "none" }}
                //   disabled={!canWrite}
              />
              <label
                htmlFor={`file-import`}
                style={{
                  // cursor: canWrite ? "pointer" : "not-allowed",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#1194DA",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {`${file?.name ? file?.name : "Choose File"}`}
                </div>
              </label>
            </div>
          )}
          {activeStep === 1 && (
            importedTemplates?.length > 0 ? (
              <Box className="imported-templates-list">
                {importedTemplates.map((template) => (
                  <Box key={template.id} className="imported-template-item">
                    <Checkbox
                      checked={selectedTemplates.includes(template.id)}
                      onChange={() => handleTemplateSelect(template.id)}
                    />
                    <div className="imported-template-name">{template?.name}</div>
                  </Box>
                ))}
              </Box>
            ) : null
          )}
        </DialogContent>
        <DialogActions className="button-wrapper">
          <Button
            onClick={() => {
              handleCancel();
            }}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            disabled={checkDisableContinueAndImportButton()}
            autoFocus
            className={`update-btn ${activeStep === 0 ? "continue-btn" : "complete-btn"}`}
            onClick={() => {
              if (activeStep === 0) handleContinue();
              if (activeStep === 1) {
                onCompleteImport();
              }
            }}
          >
            {activeStep === 0 ? "Continue" : "Complete Import"}
          </Button>
        </DialogActions>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </Dialog>
    </div>
  );
};

export default ImportTemplatesPopup;
